import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  FaBars, FaTimes, FaUserCircle, FaCheckCircle, FaWallet, FaHistory, FaSignOutAlt,
  FaChartLine, FaCoins, FaClock, FaCalendarAlt,
} from 'react-icons/fa';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import Modal from 'react-modal';
import useAuthRedirect from '../hooks/useAuthRedirect';
import { toastError, toastSuccess } from '../utils/toast';
import { money } from '../utils/format';
import Money from './ui/Money';
import { getPresetRange, startOfDayMVD, endOfDayMVD } from '../utils/dateRanges';
import Spinner, { LoadingScreen } from './ui/Spinner';
import './css/AdminPage.css';
import logo from '../assets/logo.png';

Modal.setAppElement('#root');

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement);
ChartJS.defaults.color = '#c7c7cf';
ChartJS.defaults.borderColor = 'rgba(38, 38, 46, 0.8)';

// Clases del badge según estado: pendiente (ámbar), cancelado (rojo),
// completado (verde). Antes 'cancelado' caía en el else y se pintaba verde como
// si estuviera cobrado.
const estadoBadgeClasses = (estado) => {
  const e = (estado || '').toLowerCase();
  if (e === 'pendiente') return 'bg-warn/15 text-warn border border-warn/30';
  if (e === 'cancelado') return 'bg-red/15 text-red border border-red/30';
  return 'bg-lime/15 text-lime border border-lime/30';
};

function WaiterPage() {
  const [selectedTab, setSelectedTab] = useState('main');
  const [monthlySavings, setMonthlySavings] = useState(0);
  const [withdrawHistory, setWithdrawHistory] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [userProfile, setUserProfile] = useState({});
  const openProfileModal = () => setIsProfileModalOpen(true);
  const closeProfileModal = () => setIsProfileModalOpen(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);

  // Loading / submitting states
  const [loadingTip, setLoadingTip] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingProfile, setLoadingProfile] = useState(false);

  // Pedido de retiro (modelo de reserva)
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [isRequesting, setIsRequesting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;

  useAuthRedirect();

  // Funciones para obtener datos
  const fetchCurrentTip = async () => {
    setLoadingTip(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${apiUrl}/mesero/propina`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setMonthlySavings(response.data.propina);
    } catch (error) {
      console.error('Error al obtener la propina actual:', error);
      toastError('No se pudo obtener tu propina actual.');
    } finally {
      setLoadingTip(false);
    }
  };

  // handleWithdraw removido: en el nuevo flujo el mozo no decide cuándo retirar,
  // el restaurante genera los pagos en lotes desde el panel admin.

  const fetchWithdrawHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${apiUrl}/mesero/historial`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setWithdrawHistory(response.data.historial);
    } catch (error) {
      console.error('Error al obtener el historial de retiros:', error);
      toastError('No se pudo cargar el historial de retiros.');
    } finally {
      setLoadingHistory(false);
    }
  };

  // El mozo pide retirar un monto de su saldo (lo reserva).
  const requestWithdrawal = async () => {
    const monto = Number(withdrawAmount);
    if (!Number.isFinite(monto) || monto <= 0) {
      toastError('Ingresá un monto mayor a 0.');
      return;
    }
    if (monto > monthlySavings) {
      toastError('El monto supera tu saldo disponible.');
      return;
    }
    if (isRequesting) return;
    setIsRequesting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${apiUrl}/mesero/retirar`,
        { amount: monto },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess('Pedido de retiro registrado.');
      setWithdrawAmount('');
      await fetchCurrentTip();
      await fetchWithdrawHistory();
    } catch (err) {
      console.error('Error al solicitar retiro:', err);
      toastError(err.response?.data?.error || 'No se pudo registrar el pedido.');
    } finally {
      setIsRequesting(false);
    }
  };

  // El mozo cancela su pedido pendiente (si todavía no entró en un cierre).
  const cancelRequest = async () => {
    if (isCancelling) return;
    setIsCancelling(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${apiUrl}/mesero/cancelar-retiro`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess('Pedido cancelado. Te devolvimos el saldo.');
      await fetchCurrentTip();
      await fetchWithdrawHistory();
    } catch (err) {
      console.error('Error al cancelar retiro:', err);
      toastError(err.response?.data?.error || 'No se pudo cancelar el pedido.');
    } finally {
      setIsCancelling(false);
    }
  };

  // Pedido pendiente del mozo derivado del historial:
  //  - sin agrupar (loteId NULL) → cancelable por el mozo
  //  - ya en un cierre (loteId seteado) → en proceso de pago, no cancelable
  const norm = (s) => (s || '').toLowerCase();
  const pendingUngrouped = withdrawHistory.find(
    (w) => norm(w.estado) === 'pendiente' && !w.loteId
  );
  const pendingInLote = withdrawHistory.find(
    (w) => norm(w.estado) === 'pendiente' && !!w.loteId
  );

  useEffect(() => {
    fetchCurrentTip();
    fetchWithdrawHistory();
    if (selectedTab === 'history') {
      fetchWithdrawHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  const fetchUserProfile = async () => {
    setLoadingProfile(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${apiUrl}/mesero/perfil`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUserProfile(response.data);
    } catch (error) {
      console.error('Error al obtener el perfil del usuario:', error);
      toastError('No se pudo cargar tu perfil.');
    } finally {
      setLoadingProfile(false);
    }
  };

  // Llamamos a fetchUserProfile cuando se abre el modal de perfil
  useEffect(() => {
    if (isProfileModalOpen) {
      fetchUserProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProfileModalOpen]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/';
  };

  // Cálculo de stats y widgets a partir del historial de retiros
  const waiterStats = useMemo(() => {
    const empty = {
      countAccepted: 0,
      countPending: 0,
      totalAccepted: 0,
      totalPending: 0,
      averagePerWithdraw: 0,
      lastWithdraw: null,
      daysSinceLast: null,
      chartData: [],
      thisMonthAmount: 0,
      lastMonthAmount: 0,
      deltaPct: null,
      monthLabel: new Date().toLocaleDateString('es-ES', { month: 'long', timeZone: 'America/Montevideo' }),
    };
    if (!withdrawHistory || withdrawHistory.length === 0) return empty;

    // 'cancelado' no es plata que el empleado recibió ni espera: se excluye de
    // todos los totales. 'completado' = cobrado, 'pendiente' = en proceso.
    const norm = (w) => (w.estado || '').toLowerCase();
    const relevant = withdrawHistory.filter((w) => norm(w) !== 'cancelado');
    const accepted = relevant.filter((w) => norm(w) === 'completado');
    const pending = relevant.filter((w) => norm(w) === 'pendiente');
    const totalAccepted = accepted.reduce((s, w) => s + Number(w.propina || 0), 0);
    const totalPending = pending.reduce((s, w) => s + Number(w.propina || 0), 0);
    const allSum = relevant.reduce((s, w) => s + Number(w.propina || 0), 0);
    const averagePerWithdraw = relevant.length > 0 ? allSum / relevant.length : 0;

    const sorted = [...withdrawHistory].sort(
      (a, b) => new Date(b.fecha) - new Date(a.fecha)
    );
    const lastWithdraw = sorted[0] || null;
    const daysSinceLast = lastWithdraw
      ? Math.floor((new Date() - new Date(lastWithdraw.fecha)) / (1000 * 60 * 60 * 24))
      : null;

    // Últimos 12 retiros (ordenados oldest→newest para el gráfico). Sin cancelados.
    const chartData = [...relevant]
      .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
      .slice(-12)
      .map((w) => ({
        label: new Date(w.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', timeZone: 'America/Montevideo' }),
        amount: Number(w.propina || 0),
      }));

    // Comparativa mes actual vs anterior
    const now = new Date();
    const monthRange = getPresetRange('month', now);
    const lastMonthRange = getPresetRange('lastMonth', now);
    const startThisMonth = startOfDayMVD(monthRange.start);
    const endThisMonth = endOfDayMVD(monthRange.end);
    const startLastMonth = startOfDayMVD(lastMonthRange.start);
    const endLastMonth = endOfDayMVD(lastMonthRange.end);

    const sumInRange = (start, end) =>
      relevant
        .filter((w) => {
          const f = new Date(w.fecha);
          return f >= start && f <= end;
        })
        .reduce((s, w) => s + Number(w.propina || 0), 0);

    const thisMonthAmount = sumInRange(startThisMonth, endThisMonth);
    const lastMonthAmount = sumInRange(startLastMonth, endLastMonth);
    const deltaPct =
      lastMonthAmount > 0
        ? ((thisMonthAmount - lastMonthAmount) / lastMonthAmount) * 100
        : null;
    const monthLabel = startThisMonth.toLocaleDateString('es-ES', { month: 'long', timeZone: 'America/Montevideo' });

    return {
      countAccepted: accepted.length,
      countPending: pending.length,
      totalAccepted,
      totalPending,
      averagePerWithdraw,
      lastWithdraw,
      daysSinceLast,
      chartData,
      thisMonthAmount,
      lastMonthAmount,
      deltaPct,
      monthLabel,
    };
  }, [withdrawHistory]);

  const renderContent = () => {
    switch (selectedTab) {
      case 'main':
        return (
          <div className="max-w-5xl mx-auto">
            <div className="mb-8">
              <h2 className="si-h1">Mis ahorros</h2>
              <p className="si-body mt-1">
                Consultá tus propinas acumuladas y retirá cuando lo necesites.
              </p>
            </div>

            {loadingTip && (
              <div className="si-card p-6 mb-6">
                <LoadingScreen label="Cargando tu propina..." />
              </div>
            )}

            {/* Card principal con monto */}
            {!loadingTip && (
            <div className="si-card p-8 mb-6 relative overflow-hidden">
              <div
                aria-hidden="true"
                className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-10 blur-2xl"
                style={{ background: '#c8f06a' }}
              />
              <div className="relative">
                <div className="text-xs uppercase tracking-wider text-ink-3 mb-2">
                  Tu propina acumulada
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="si-display" style={{ color: '#c8f06a' }}>
                    <Money value={monthlySavings} />
                  </span>
                </div>

                <p className="text-xs text-ink-3 mt-4 max-w-md">
                  Pedí abajo cuánto querés retirar. El restaurante lo paga en el próximo cierre y el saldo se actualiza acá.
                </p>
              </div>
            </div>
            )}

            {/* Solicitar retiro */}
            {!loadingTip && !loadingHistory && (
              <div className="si-card p-6 mb-6">
                {pendingUngrouped ? (
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="text-sm font-semibold text-ink">Tenés un pedido de retiro pendiente</div>
                      <div className="text-2xl font-bold mt-1" style={{ color: '#b28cf5' }}>
                        <Money value={Number(pendingUngrouped.propina)} />
                      </div>
                      <div className="text-xs text-ink-3 mt-1 max-w-md">
                        Esperando que el restaurante lo procese. Si lo cancelás, te devolvemos el saldo.
                      </div>
                    </div>
                    <button onClick={cancelRequest} disabled={isCancelling} className="si-btn-danger">
                      {isCancelling ? (<><Spinner size={14} className="mr-2" />Cancelando...</>) : 'Cancelar pedido'}
                    </button>
                  </div>
                ) : pendingInLote ? (
                  <div>
                    <div className="text-sm font-semibold text-ink">Tu retiro está en proceso de pago</div>
                    <div className="text-2xl font-bold mt-1" style={{ color: '#c8f06a' }}>
                      <Money value={Number(pendingInLote.propina)} />
                    </div>
                    <div className="text-xs text-ink-3 mt-1 max-w-md">
                      El restaurante ya lo incluyó en un cierre. Cuando confirme el pago, lo vas a ver como cobrado.
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-sm font-semibold text-ink mb-1">Solicitar un retiro</div>
                    <div className="text-xs text-ink-3 mb-3 max-w-md">
                      Pedí el monto que quieras retirar (hasta <Money value={monthlySavings} />). El restaurante lo paga en el próximo cierre.
                    </div>
                    <div className="flex items-end gap-3 flex-wrap">
                      <div>
                        <label className="si-label">Monto</label>
                        <input
                          type="number"
                          min="0"
                          max={monthlySavings}
                          value={withdrawAmount}
                          onChange={(e) => setWithdrawAmount(e.target.value)}
                          className="si-input"
                          placeholder="0"
                        />
                      </div>
                      <button
                        onClick={requestWithdrawal}
                        disabled={isRequesting || monthlySavings <= 0}
                        className="si-btn-primary"
                      >
                        {isRequesting ? (<><Spinner size={14} className="mr-2" />Enviando...</>) : 'Solicitar retiro'}
                      </button>
                    </div>
                    {monthlySavings <= 0 && (
                      <div className="text-xs text-ink-3 mt-2">No tenés saldo disponible para retirar.</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 3 stats compactas */}
            {!loadingHistory && (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                <div className="si-card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-wider text-ink-3">Retiros cobrados</div>
                    <div
                      className="w-7 h-7 rounded-md grid place-items-center"
                      style={{ background: 'rgba(200, 240, 106, 0.12)' }}
                    >
                      <FaCheckCircle className="text-xs" style={{ color: '#c8f06a' }} />
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-ink">{waiterStats.countAccepted}</div>
                </div>

                <div className="si-card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-wider text-ink-3">Total cobrado</div>
                    <div
                      className="w-7 h-7 rounded-md grid place-items-center"
                      style={{ background: 'rgba(178, 140, 245, 0.12)' }}
                    >
                      <FaCoins className="text-xs" style={{ color: '#b28cf5' }} />
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-ink">
                    <Money value={waiterStats.totalAccepted} />
                  </div>
                </div>

                <div className="si-card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-wider text-ink-3">Promedio</div>
                    <div
                      className="w-7 h-7 rounded-md grid place-items-center"
                      style={{ background: 'rgba(178, 140, 245, 0.12)' }}
                    >
                      <FaChartLine className="text-xs" style={{ color: '#b28cf5' }} />
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-ink">
                    ${waiterStats.averagePerWithdraw.toFixed(0)}
                  </div>
                  <div className="text-[11px] text-ink-3 mt-0.5">por retiro</div>
                </div>
              </div>
            )}

            {/* Widgets: gráfico + comparativa + último retiro */}
            {!loadingHistory && withdrawHistory.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Widget 1 — Evolución de retiros (line chart) */}
                <div className="si-card p-5 lg:col-span-2">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-ink-3">Evolución de tus retiros</div>
                      <div className="text-sm font-semibold text-ink mt-0.5">
                        Últimos {waiterStats.chartData.length} retiros
                      </div>
                    </div>
                    <div
                      className="w-9 h-9 rounded-lg grid place-items-center"
                      style={{ background: 'rgba(200, 240, 106, 0.12)' }}
                    >
                      <FaChartLine style={{ color: '#c8f06a' }} />
                    </div>
                  </div>
                  <div style={{ height: 200 }}>
                    <Line
                      data={{
                        labels: waiterStats.chartData.map((d) => d.label),
                        datasets: [
                          {
                            data: waiterStats.chartData.map((d) => d.amount),
                            borderColor: '#c8f06a',
                            backgroundColor: 'rgba(200, 240, 106, 0.15)',
                            pointBackgroundColor: '#c8f06a',
                            pointBorderColor: '#c8f06a',
                            tension: 0.35,
                            fill: true,
                            borderWidth: 2,
                            pointRadius: 3,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            backgroundColor: '#17171d',
                            borderColor: '#26262e',
                            borderWidth: 1,
                            titleColor: '#f5f5f7',
                            bodyColor: '#c7c7cf',
                            padding: 10,
                            callbacks: {
                              label: (ctx) => money(Number(ctx.parsed.y)),
                            },
                          },
                        },
                        scales: {
                          x: {
                            grid: { display: false },
                            ticks: { color: '#7a7a82', font: { size: 10 } },
                          },
                          y: {
                            grid: { color: 'rgba(38, 38, 46, 0.5)' },
                            ticks: {
                              color: '#7a7a82',
                              font: { size: 10 },
                              callback: (v) => `$${v}`,
                            },
                            beginAtZero: true,
                          },
                        },
                      }}
                    />
                  </div>
                </div>

                {/* Widget 2 — Este mes vs anterior */}
                <div className="si-card p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-ink-3">Este mes</div>
                      <div className="text-sm font-semibold text-ink mt-0.5 capitalize">
                        {waiterStats.monthLabel}
                      </div>
                    </div>
                    <div
                      className="w-9 h-9 rounded-lg grid place-items-center"
                      style={{ background: 'rgba(178, 140, 245, 0.12)' }}
                    >
                      <FaCalendarAlt style={{ color: '#b28cf5' }} />
                    </div>
                  </div>
                  <div className="text-3xl font-bold tracking-tightish text-ink mb-2">
                    <Money value={waiterStats.thisMonthAmount} />
                  </div>
                  {waiterStats.deltaPct === null ? (
                    <div className="text-xs text-ink-3 mb-4">
                      Sin retiros del mes anterior para comparar.
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mb-4">
                      <span
                        className="inline-flex items-center gap-1 text-sm font-semibold px-2 py-0.5 rounded-md"
                        style={{
                          background:
                            waiterStats.deltaPct >= 0
                              ? 'rgba(200, 240, 106, 0.12)'
                              : 'rgba(239, 68, 68, 0.12)',
                          color: waiterStats.deltaPct >= 0 ? '#c8f06a' : '#ef4444',
                        }}
                      >
                        {waiterStats.deltaPct >= 0 ? '▲' : '▼'}
                        {Math.abs(waiterStats.deltaPct).toFixed(1)}%
                      </span>
                      <span className="text-xs text-ink-3">vs mes anterior</span>
                    </div>
                  )}
                  <div className="pt-3 border-t border-line">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-ink-3">Mes anterior</span>
                      <span className="text-ink font-medium">
                        <Money value={waiterStats.lastMonthAmount} />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Último retiro - banner secundario */}
            {!loadingHistory && waiterStats.lastWithdraw && (
              <div className="si-card p-4 mt-4 flex items-center gap-4">
                <div
                  className="w-10 h-10 rounded-lg grid place-items-center flex-shrink-0"
                  style={{ background: 'rgba(178, 140, 245, 0.12)' }}
                >
                  <FaClock style={{ color: '#b28cf5' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wider text-ink-3 mb-0.5">
                    Último retiro
                  </div>
                  <div className="text-sm text-ink">
                    <span className="font-semibold"><Money value={waiterStats.lastWithdraw.propina} /></span>
                    <span className="text-ink-3"> · </span>
                    <span className="text-ink-2">
                      {new Date(waiterStats.lastWithdraw.fecha).toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        timeZone: 'America/Montevideo',
                      })}
                    </span>
                    {waiterStats.daysSinceLast !== null && (
                      <>
                        <span className="text-ink-3"> · </span>
                        <span className="text-ink-2">
                          hace {waiterStats.daysSinceLast === 0
                            ? 'hoy'
                            : waiterStats.daysSinceLast === 1
                            ? '1 día'
                            : `${waiterStats.daysSinceLast} días`}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${estadoBadgeClasses(waiterStats.lastWithdraw.estado)}`}
                >
                  {waiterStats.lastWithdraw.estado.charAt(0).toUpperCase() +
                    waiterStats.lastWithdraw.estado.slice(1)}
                </span>
              </div>
            )}

            {/* Empty state si no hay retiros aún */}
            {!loadingHistory && withdrawHistory.length === 0 && (
              <div className="si-card p-8 text-center">
                <div
                  className="w-14 h-14 rounded-2xl grid place-items-center mx-auto mb-3"
                  style={{ background: 'rgba(178, 140, 245, 0.12)' }}
                >
                  <FaWallet className="text-xl" style={{ color: '#b28cf5' }} />
                </div>
                <div className="text-sm text-ink-2 mb-1">Aún no realizaste ningún retiro.</div>
                <div className="text-xs text-ink-3">
                  Cuando hagas tu primer retiro, vas a ver acá tus estadísticas y evolución.
                </div>
              </div>
            )}
          </div>
        );

      case 'history':
        return (
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <h2 className="si-h1">Historial de retiros</h2>
              <p className="si-body mt-1">
                Todos los retiros que has solicitado.
              </p>
            </div>

            {loadingHistory ? (
              <div className="si-card p-6">
                <LoadingScreen label="Cargando historial..." />
              </div>
            ) : withdrawHistory.length === 0 ? (
              <div className="si-card p-10 text-center">
                <p className="text-ink-3">Todavía no pediste ningún retiro.</p>
              </div>
            ) : (
              <>
                {/* Tabla en pantallas medianas y grandes */}
                <div className="hidden sm:block si-card overflow-hidden">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-line">
                        <th className="py-3 px-5 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Fecha</th>
                        <th className="py-3 px-5 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Cantidad retirada</th>
                        <th className="py-3 px-5 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {withdrawHistory.map((entry, idx) => (
                        <tr
                          key={entry.id}
                          className={`${idx !== withdrawHistory.length - 1 ? 'border-b border-line' : ''} hover:bg-surface-2/50 transition-colors`}
                        >
                          <td className="py-3 px-5 text-sm text-ink-2">
                            {new Date(entry.fecha).toLocaleString('es-ES', { timeZone: 'America/Montevideo' })}
                          </td>
                          <td className="py-3 px-5 text-sm font-semibold text-right" style={{ color: '#c8f06a' }}>
                            <Money value={entry.propina} />
                          </td>
                          <td className="py-3 px-5">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${estadoBadgeClasses(entry.estado)}`}
                            >
                              {entry.estado.charAt(0).toUpperCase() + entry.estado.slice(1)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Vista tipo "tarjetas" en pantallas pequeñas */}
                <div className="block sm:hidden space-y-3">
                  {withdrawHistory.map((entry) => (
                    <div key={entry.id} className="si-card p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="text-sm text-ink-2">
                          {new Date(entry.fecha).toLocaleString('es-ES', { timeZone: 'America/Montevideo' })}
                        </div>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${estadoBadgeClasses(entry.estado)}`}
                        >
                          {entry.estado.charAt(0).toUpperCase() + entry.estado.slice(1)}
                        </span>
                      </div>
                      <div className="text-2xl font-bold" style={{ color: '#c8f06a' }}>
                        <Money value={entry.propina} />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-bg text-ink">
      {/* Navbar */}
      <header className="bg-surface border-b border-line sticky top-0 z-10">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16">
          {/* Brand */}
          <div className="flex items-center gap-3">
            <img
              src={logo}
              alt="ServiceIn"
              className="w-9 h-9 rounded-lg object-contain"
            />
            <h1 className="si-h2 hidden sm:block">ServiceIn</h1>
          </div>

          {/* Botón Hamburguesa para Móviles */}
          <div className="flex items-center md:hidden">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-lg text-ink-2 hover:text-ink hover:bg-surface-2 focus:outline-none transition"
              aria-label="Abrir menú principal"
            >
              {menuOpen ? <FaTimes className="h-5 w-5" /> : <FaBars className="h-5 w-5" />}
            </button>
          </div>

          {/* Menú Desktop */}
          <div className="hidden md:flex items-center gap-1">
            <button
              onClick={() => {
                setSelectedTab('main');
                setMenuOpen(false);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                selectedTab === 'main'
                  ? 'bg-purple/15 text-purple border border-purple/30'
                  : 'text-ink-2 hover:text-ink hover:bg-surface-2 border border-transparent'
              }`}
            >
              <FaWallet className="text-xs" />
              Mis ahorros
            </button>
            <button
              onClick={() => {
                setSelectedTab('history');
                setMenuOpen(false);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                selectedTab === 'history'
                  ? 'bg-purple/15 text-purple border border-purple/30'
                  : 'text-ink-2 hover:text-ink hover:bg-surface-2 border border-transparent'
              }`}
            >
              <FaHistory className="text-xs" />
              Historial
            </button>
          </div>

          {/* Botón de Perfil */}
          <div className="hidden md:flex items-center">
            <button
              onClick={openProfileModal}
              className="text-2xl text-ink-2 hover:text-purple transition"
              aria-label="Perfil"
            >
              <FaUserCircle />
            </button>
          </div>
        </div>

        {/* Menú Móvil */}
        {menuOpen && (
          <div className="md:hidden bg-surface border-t border-line">
            <div className="px-3 py-3 space-y-1">
              <button
                onClick={() => {
                  setSelectedTab('main');
                  setMenuOpen(false);
                }}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                  selectedTab === 'main'
                    ? 'bg-purple/15 text-purple'
                    : 'text-ink-2 hover:bg-surface-2'
                }`}
              >
                <FaWallet />
                Mis ahorros
              </button>
              <button
                onClick={() => {
                  setSelectedTab('history');
                  setMenuOpen(false);
                }}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                  selectedTab === 'history'
                    ? 'bg-purple/15 text-purple'
                    : 'text-ink-2 hover:bg-surface-2'
                }`}
              >
                <FaHistory />
                Historial
              </button>
              <button
                onClick={() => {
                  openProfileModal();
                  setMenuOpen(false);
                }}
                className="w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium text-ink-2 hover:bg-surface-2 transition flex items-center gap-2"
              >
                <FaUserCircle />
                Perfil
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Contenido Principal */}
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        {renderContent()}
      </main>

      {/* Modal de perfil */}
      <Modal
        isOpen={isProfileModalOpen}
        onRequestClose={closeProfileModal}
        className="bg-surface border border-line rounded-2xl shadow-card mx-4 max-w-xl w-full overflow-hidden relative outline-none"
        overlayClassName="ReactModal__Overlay"
      >
        <div className="px-6 py-4 border-b border-line flex items-center justify-between">
          <h2 className="si-h2">Mi perfil</h2>
          <button
            onClick={closeProfileModal}
            className="text-ink-3 hover:text-ink transition p-1"
            aria-label="Cerrar"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-6">
          <div className="flex justify-center mb-5">
            <div
              className="w-24 h-24 rounded-2xl grid place-items-center"
              style={{ background: 'rgba(178, 140, 245, 0.15)' }}
            >
              <FaUserCircle className="text-6xl" style={{ color: '#b28cf5' }} />
            </div>
          </div>
          {loadingProfile ? (
            <LoadingScreen label="Cargando tu perfil..." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <ProfileField label="Documento" value={userProfile.Ci} />
              <ProfileField label="Email" value={userProfile.email} />
              <ProfileField label="Nombre" value={userProfile.Nombre} />
              <ProfileField label="Teléfono" value={userProfile.telefono} />
              <ProfileField label="Apellido" value={userProfile.Apellido} />
              <ProfileField label="Cuenta bancaria" value={userProfile.nCuenta} />
            </div>
          )}
        </div>

        <div className="border-t border-line px-6 py-4 flex justify-end gap-3">
          <button
            onClick={handleLogout}
            className="si-btn-danger"
          >
            <FaSignOutAlt className="mr-2" />
            Cerrar sesión
          </button>
          <button
            onClick={closeProfileModal}
            className="si-btn-secondary"
          >
            Cerrar
          </button>
        </div>
      </Modal>

      {/* Modal de confirmación de retiro */}
      <Modal
        isOpen={isWithdrawModalOpen}
        onRequestClose={() => setIsWithdrawModalOpen(false)}
        className="bg-surface border border-line rounded-2xl shadow-card mx-4 max-w-md w-full p-6 relative outline-none"
        overlayClassName="ReactModal__Overlay"
      >
        <div className="flex flex-col items-center text-center">
          <div
            className="w-16 h-16 rounded-full grid place-items-center mb-4"
            style={{ background: 'rgba(200, 240, 106, 0.15)' }}
          >
            <FaCheckCircle className="text-4xl" style={{ color: '#c8f06a' }} />
          </div>
          <h2 className="text-xl font-bold text-ink mb-2">Retiro realizado</h2>
          <p className="text-sm text-ink-2 mb-6">
            En un plazo de 48 horas hábiles recibirás tu propina. Te enviaremos un correo electrónico para confirmar.
          </p>
          <button
            onClick={() => setIsWithdrawModalOpen(false)}
            className="si-btn-primary w-full"
          >
            Listo
          </button>
        </div>
      </Modal>
    </div>
  );
}

function ProfileField({ label, value }) {
  return (
    <div className="bg-surface-2 border border-line rounded-xl px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-wider text-ink-3 mb-0.5">{label}</div>
      <div className="text-sm text-ink truncate">{value || '—'}</div>
    </div>
  );
}

export default WaiterPage;
